/* EXC fitment page (sections/exc-fitment.liquid).
   Without this script every bike sheet shows, one under another. With it:
   - brand pills filter the bike tiles
   - tapping a bike shows only its sheet and puts ?bike=<slug> in the address,
     so the link can be shared
   - ?part=<product handle> (the Check fitment link on product pages) shows only
     the bikes that part fits, and opens the sheet straight away when it is one
   Notes: hq/projects/excdesigns/theme-build-notes.md (2026-10-22) */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class ExcFitment extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.items = [...this.querySelectorAll('[data-fit-bike-item]')];
    this.tiles = [...this.querySelectorAll('[data-fit-bike]')];
    this.sheets = [...this.querySelectorAll('[data-fit-sheet]')];
    this.brandBtns = [...this.querySelectorAll('[data-fit-brand]')];
    this.prompt = this.querySelector('[data-fit-prompt]');
    this.partNote = this.querySelector('[data-fit-part-note]');
    this.partText = this.querySelector('[data-fit-part-text]');

    this.tiles.forEach((tile) => tile.addEventListener('click', () => this.select(tile.dataset.fitBike, true)));
    this.brandBtns.forEach((btn) => btn.addEventListener('click', () => this.filterBrand(btn.dataset.fitBrand)));
    this.querySelector('[data-fit-reset]')?.addEventListener('click', () => this.clearPart());

    const params = new URLSearchParams(location.search);
    const bike = params.get('bike');
    const part = params.get('part');

    if (part) this.filterPart(part);
    if (bike && this.sheet(bike)) {
      this.select(bike, false);
    } else if (!this.current) {
      this.showNone();
    }
  }

  sheet(slug) {
    return this.sheets.find((s) => s.dataset.fitSheet === slug);
  }

  showNone() {
    this.current = null;
    this.sheets.forEach((s) => (s.hidden = true));
    this.tiles.forEach((t) => t.setAttribute('aria-pressed', 'false'));
    if (this.prompt) this.prompt.hidden = false;
  }

  select(slug, fromTap) {
    const target = this.sheet(slug);
    if (!target) return;
    this.current = slug;
    this.sheets.forEach((s) => (s.hidden = s !== target));
    this.tiles.forEach((t) => t.setAttribute('aria-pressed', String(t.dataset.fitBike === slug)));
    if (this.prompt) this.prompt.hidden = true;

    const url = new URL(location.href);
    url.searchParams.set('bike', slug);
    history.replaceState(null, '', url);

    if (fromTap) {
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
    }
  }

  filterBrand(brand) {
    this.brandBtns.forEach((b) => {
      const on = b.dataset.fitBrand === brand;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', String(on));
    });
    this.items.forEach((item) => (item.hidden = brand !== '' && item.dataset.brand !== brand));
    // A sheet for a bike that just got filtered out goes too.
    if (this.current) {
      const item = this.items.find((i) => i.querySelector(`[data-fit-bike="${this.current}"]`));
      if (item && item.hidden) this.showNone();
    }
  }

  filterPart(handle) {
    const matches = this.items.filter((item) => (item.dataset.parts || '').split(' ').includes(handle));
    if (!matches.length) return;
    this.items.forEach((item) => (item.hidden = !matches.includes(item)));
    if (this.partNote) {
      this.partText.textContent =
        matches.length === 1 ? 'This part fits 1 bike.' : `This part fits these ${matches.length} bikes.`;
      this.partNote.hidden = false;
    }
    if (matches.length === 1) this.select(matches[0].querySelector('[data-fit-bike]').dataset.fitBike, false);
  }

  clearPart() {
    this.items.forEach((item) => (item.hidden = false));
    if (this.partNote) this.partNote.hidden = true;
    this.brandBtns.forEach((b, i) => {
      b.classList.toggle('is-active', i === 0);
      b.setAttribute('aria-pressed', String(i === 0));
    });
    const url = new URL(location.href);
    url.searchParams.delete('part');
    history.replaceState(null, '', url);
  }
}

if (!customElements.get('exc-fitment')) customElements.define('exc-fitment', ExcFitment);
